import React, { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useSearchParams } from 'react-router-dom';
import RouterErrorPage from '../pages/RouterErrorPage';
import Layout from '../pages/LandingPage/Layout.js';
import LazyRouteWrapper from '../components/LazyRouteWrapper';
import {
  LEGACY_LANDING_REDIRECTS,
  shouldRedirectLegacyHomeQuery,
} from '../config/legacyLandingRedirects';

// Import only landing-specific lazy-loaded routes
import * as LazyRoutes from '../utils/lazyRoutes';
import useGetLoggedInUser from '../Hooks/LoggedInUser/useGetLoggedInUser';
import { navigateToDashboard, getDashboardUrl } from '../utils/navigation';

// Component to redirect to dashboard
const RedirectToDashboard = ({ path }) => {
  useEffect(() => {
    navigateToDashboard(path || window.location.pathname);
  }, [path]);
  return null;
};

// Old landing /explore* URLs → dashboard guest marketplace (/explore).
const RedirectToExplore = () => {
  useEffect(() => {
    window.location.replace(getDashboardUrl('/explore'));
  }, []);
  return null;
};

const RedirectToUrl = ({ url }) => {
  useEffect(() => {
    if (url) window.location.replace(url);
  }, [url]);
  return null;
};

const HomePageRoute = () => {
  const [searchParams] = useSearchParams();
  if (shouldRedirectLegacyHomeQuery(searchParams)) {
    return <Navigate to={{ pathname: '/', search: '' }} replace />;
  }
  return (
    <Layout>
      {/* Keeps the footer below the first screen while the page paints, so it does not jump down. */}
      <div style={{ minHeight: 'calc(100vh + 120px)' }}>
        <LazyRouteWrapper>
          <LazyRoutes.HomePage />
        </LazyRouteWrapper>
      </div>
    </Layout>
  );
};

// ---------------------------------------------------------------------------
// Route helpers — keep the table DRY and make it obvious that each blog post has
// exactly ONE canonical route.
// ---------------------------------------------------------------------------

// Standalone page (component renders its own Navbar/Footer).
const wrapped = (path, Component) => ({
  path,
  element: (
    <LazyRouteWrapper>
      <Component />
    </LazyRouteWrapper>
  ),
  errorElement: <RouterErrorPage />,
});

// Page rendered inside the shared <Layout> chrome.
const page = (path, Component) => ({
  path,
  element: (
    <Layout>
      <LazyRouteWrapper>
        <Component />
      </LazyRouteWrapper>
    </Layout>
  ),
  errorElement: <RouterErrorPage />,
});

// Old landing URL that now funnels to the dashboard /explore app.
const exploreRedirect = path => ({
  path,
  element: <RedirectToExplore />,
  errorElement: <RouterErrorPage />,
});

/** Legacy redirects mirrored from SEO sheet (dev + SPA fallback). */
const legacyRedirectRoutes = LEGACY_LANDING_REDIRECTS.map(({ from, to }) => ({
  path: from,
  element: /^https?:\/\//i.test(to) ? (
    <RedirectToUrl url={to} />
  ) : (
    <Navigate to={to} replace />
  ),
  errorElement: <RouterErrorPage />,
}));

// Canonical blog routes. Each post is reachable ONLY at /blog/<slug>.
// The legacy duplicates (/blogpage/<slug> and bare /<slug>) are 301-redirected to
// these at the server layer — see nginx.conf and public/_redirects. React Router
// redirects above mirror the same mapping for localhost (CRA dev server ignores _redirects).
const blogRoutes = [
  wrapped('/blog/what-is-b2b-barter', LazyRoutes.WhatIsB2B),
  wrapped('/blog/types-of-b2b-marketplaces', LazyRoutes.Typesofb2bmarketplaces),
  wrapped('/blog/how-barter-can-grow-your-business-community', LazyRoutes.HowBarterCanGrowYourBusinessCommunity),
  wrapped('/blog/barters-digital-evolution-from-traditional-to-modern', LazyRoutes.BarterDigitalEvolutionFromTraditionalToModern),
  wrapped('/blog/future-of-b2b-barter-cashless-business-exchange', LazyRoutes.TheFutureofB2BBarterCashlessBusiness),
  wrapped('/blog/enhance-your-business-potential-with-b2b-barter-marketplace-platforms', LazyRoutes.EnhanceYourBusinessPotentialwithB2BBarter),
  wrapped('/blog/barter-networks-indian-b2b-trade', LazyRoutes.WhyBarterNetworksAreRising),
  wrapped('/blog/barter-exchange-benefits-for-businesses', LazyRoutes.AdvantagesofBarterExchangeforModernBusinesses),
  wrapped('/blog/how-modern-business-exchange-value', LazyRoutes.HowModernBusinessesExchangeValueandGrowSmarter),
  wrapped('/blog/get-work-done-without-cashflow', LazyRoutes.GetWorkDoneWithoutCashOutflow),
  wrapped('/blog/corporate-barter-smart-strategy-for-growth-competitive-markets', LazyRoutes.CorporateBarterASmartStrategy),
  wrapped('/blog/B2b-barter-builds-long-term-business-relationships', LazyRoutes.HowB2BBarterBuildsLongTermBusinessRelationships),
  wrapped('/blog/how-barter-system-drives-zero-waste-communities', LazyRoutes.HowtheBarterSystemPromotesSustainability),
  wrapped('/blog/how-business-to-business-exchange-works', LazyRoutes.HowBusinesstoBusinessExchangeWorkTodayEconomy),
  wrapped('/blog/is-the-barter-system-legal-in-india', LazyRoutes.IsBarterLegal),
  wrapped('/blog/exploring-the-online-bartering-platform-market-trends-benefits-and-top-platforms', LazyRoutes.ExploringOnlineBartering),
  wrapped('/blog/barter-for-service-and-goods', LazyRoutes.HowtoFindBarterMarket),
  wrapped('/blog/the-role-of-trust-in-barter-deals', LazyRoutes.RoleOfTrust),
  wrapped('/blog/the-benefits-of-barter-service-for-small-businesses', LazyRoutes.BenefitsOfBarter),
  wrapped('/blog/how-does-barter-work-in-modern-india', LazyRoutes.Howbarter),
  wrapped('/blog/advantages-and-disadvantages-of-the-barter-system-of-exchange', LazyRoutes.EconomyInIndia),
  wrapped('/blog/barter-trade-companies', LazyRoutes.BartertradeCompanies),
  wrapped('/blog/barter-exchange-goods-without-involving-money', LazyRoutes.BenefitsOfBartering),
  wrapped('/blog/difference-between-barter-exchange-and-money-exchange', LazyRoutes.FutureOfBarter),
  wrapped('/blog/modern-barter-trade-in-india', LazyRoutes.ModernBarterTradeinIndia),
  wrapped('/blog/advantages-of-the-barter-system-for-modern-businesses-in-india', LazyRoutes.AdvantagesoftheBarterSystemforModernBusinessesinIndia),
  wrapped('/blog/what-lifestyle-brands-can-trade-on-the-bxi-b2b-barter-platform', LazyRoutes.WhatLifestyleBrandsCanTradeonBXI),
  wrapped('/blog/how-consultants-and-professionals-use-barter-trade-platforms', LazyRoutes.HowConsultantsandProfessionalsUseBarterTradePlatforms),
  wrapped('/blog/optimize-business-expenses-without-cutting-operations', LazyRoutes.OptimizeBusinessExpensesWithoutCuttingOperations),
  wrapped('/blog/business-guide-managing-cash-flow-during-slow-seasons', LazyRoutes.TheBusinessOwnerGuidetoManagingCashFlowDuringSlowSeasons),
  wrapped('/blog/save-marketing-budget-media-buying', LazyRoutes.HowtoSaveYourMarketingBudgetonMediaBuying),
  wrapped('/blog/guide-to-trading-on-a-b2b-barter-platform', LazyRoutes.AStepbyStepGuidetoTradingonaB2BBarterPlatform),
  wrapped('/blog/corporate-gifting-without-impacting-marketing-budgets', LazyRoutes.CorporateGiftingWithoutImpactingMarketingBudgets),
  wrapped('/blog/cfo-guide-smarter-procurement-better-cash-flow', LazyRoutes.TheCFOGuidetoSmarterProcurementandBetterCashFlow),
];

const landingRoutes = [
  exploreRedirect('/explore'),
  page('/bookyourdemo', LazyRoutes.DemoBooking),
  {
    path: '/',
    element: <HomePageRoute />,
    errorElement: <RouterErrorPage />,
  },

  // Legacy redirects first so historical URLs resolve before 404.
  // Includes industry aliases + /home/marketplace/category/* → /marketplace
  // (CRA ignores _redirects/nginx locally).
  ...legacyRedirectRoutes,
  ...blogRoutes,
  wrapped('/blog', LazyRoutes.BlogPage),

  // Legal / utility
  wrapped('/termsconditions', LazyRoutes.TermsAndConditions),
  wrapped('/privacypolicy', LazyRoutes.PrivacyPolicy),
  wrapped('/legalpolicy', LazyRoutes.LegalPoliciy),
  wrapped('/renewal', LazyRoutes.MembershipRenewal),

  // Core marketing pages (inside shared Layout)
  page('/faq', LazyRoutes.FaqPage),
  page('/about', LazyRoutes.AboutPage),
  page('/contact-us', LazyRoutes.ContactUs),
  page('/learnbarter', LazyRoutes.MainLearnBarter),
  page('/marketplace', LazyRoutes.NewMarketplace),

  // Explore / marketplace browsing now lives on the dashboard app
  exploreRedirect('/explore/marketplaceproductlist/:id'),
  exploreRedirect('/explore/productdetail/:id'),
  exploreRedirect('/explore/:categoryName'),

  // Redirect login/register routes to dashboard
  {
    path: '/login',
    element: <RedirectToDashboard path="/login" />,
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/login2fa',
    element: <RedirectToDashboard path="/login2fa" />,
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/register',
    element: <RedirectToDashboard path="/register" />,
    errorElement: <RouterErrorPage />,
  },
  {
    path: '/createaccount',
    element: <RedirectToDashboard path="/createaccount" />,
    errorElement: <RouterErrorPage />,
  },
];

const router = createBrowserRouter(landingRoutes, {
  basename: '/',
});

export default function LandingRouter() {
  const { data: AuthUserData } = useGetLoggedInUser();

  useEffect(() => {
    // Redirect logged-in users to dashboard
    if (AuthUserData?.data) {
      navigateToDashboard();
    }
  }, [AuthUserData]);

  return <RouterProvider router={router} />;
}
